import { useSidebar } from '../../../base/sidebar/sidebar-context';
import { NavigationItem } from './NavigationItem';
import { NoddiSidebarFooter } from './NoddiSidebarFooter';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarRail
} from './Sidebar';
import { PartnerSidebarMenuSection, SidebarMenuSection } from './types';

// Type guard to check if section is PartnerSidebarMenuSection
const isPartnerSidebarMenuSection = (
  section: SidebarMenuSection | PartnerSidebarMenuSection
): section is PartnerSidebarMenuSection => {
  return 'groupLabel' in section;
};

/** Structural, not the API record: the design system knows a name and an email, nothing more. */
type SidebarUser = { firstName: string; lastName: string; email?: string | null };

export type NoddiSidebarProps = {
  navItems: SidebarMenuSection[] | PartnerSidebarMenuSection[];
  onLogout?: () => void;
  profilePath: string;
  profileLabel?: string;
  logoutLabel?: string;
  user?: SidebarUser | null;
  className?: string;
  contentClassName?: string;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  side?: 'left' | 'right';
  mobileSide?: 'left' | 'right';
  devSettingsPath?: string;
  extraMenuItems?: React.ReactNode;
};

export function NoddiSidebar({
  navItems,
  onLogout,
  profilePath,
  profileLabel = 'My account',
  logoutLabel = 'Log out',
  user,
  className,
  contentClassName,
  header,
  footer,
  side = 'left',
  mobileSide = 'right',
  devSettingsPath,
  extraMenuItems
}: NoddiSidebarProps) {
  const userInfo = user
    ? {
        name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        email: user.email || '',
        avatarUrl: undefined
      }
    : {
        name: '',
        email: '',
        avatarUrl: '/placeholder.svg?height=64&width=64'
      };

  const filteredNavItems = navItems.filter((section) => section.items.some((item) => !item.hide));

  const { isMobile } = useSidebar();
  const currentSide = isMobile ? mobileSide : side;

  return (
    <Sidebar variant='sidebar' collapsible='icon' className={className} side={currentSide}>
      {header && <SidebarHeader>{header}</SidebarHeader>}

      <SidebarContent className={contentClassName}>
        {filteredNavItems.map((section, sectionIndex) => (
          <SidebarGroup key={`section-${sectionIndex}`}>
            {isPartnerSidebarMenuSection(section) && section.groupLabel && (
              <SidebarGroupLabel>{section.groupLabel}</SidebarGroupLabel>
            )}
            <SidebarGroupContent>
              <SidebarMenu>
                {section.items.map((item, itemIndex) => (
                  <NavigationItem key={`item-${sectionIndex}-${itemIndex}`} item={item} />
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter>
        <NoddiSidebarFooter
          onLogout={onLogout}
          profilePath={profilePath}
          profileLabel={profileLabel}
          logoutLabel={logoutLabel}
          user={userInfo}
          devSettingsPath={devSettingsPath}
          extraMenuItems={extraMenuItems}
        />
        {footer}
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
